import React, { useState, useEffect, useMemo } from 'react';
import { Card, Spinner, Alert, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { formatDateWithLocale } from '../shared/utils';
import { fetchWeatherData } from '../shared/weatherApi';
import { clog, cerror } from '../shared/consoleLogger';
import ScrollableTabs from './ScrollableTabs';

/**
 * Get current local time for a timezone
 * @param {string} timezone Timezone string (e.g., "Europe/Paris")
 * @returns {string} Formatted time string
 */
function getLocalTime(timezone) {
  if (!timezone) return null;
  
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    return formatter.format(now);
  } catch (error) {
    console.error('Error formatting time for timezone:', timezone, error);
    return null;
  }
}

/**
 * Weather Tab component that displays forecast data for cities in the plan
 * Uses Open-Meteo API to fetch weather data (no API key required)
 * 
 * @param {Object} props Component props
 * @param {Object} props.parsedData The parsed plan data
 * @param {boolean} props.isActive Whether this tab is currently active
 */
export default function WeatherTab({ parsedData, isActive = false }) {
  const { t } = useTranslation();
  const [weatherData, setWeatherData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dataFetched, setDataFetched] = useState(false);
  const [activeCityTab, setActiveCityTab] = useState(null);
  
  // Extract cities using useMemo - recomputes when parsedData changes
  // Returns an array of objects with { apiCity, displayCity } where apiCity uses English names
  const cities = useMemo(() => {
    if (!parsedData) return [];
    
    try {
      const cityMap = new Map(); // Map to store unique cities: key = English name for API, value = display name
      
      // Extract from plan locations
      if (parsedData.location) {
        const city = parsedData.location.cityInEnglish || parsedData.location.city;
        const country = parsedData.location.countryInEnglish || parsedData.location.country;
        const displayCity = parsedData.location.city;
        const displayCountry = parsedData.location.country;
        
        if (city) {
          const apiKey = `${city}, ${country || ''}`;
          const displayKey = `${displayCity}, ${displayCountry || ''}`;
          cityMap.set(apiKey, displayKey);
        }
      }
      
      // Extract from activities locations
      if (parsedData.days && Array.isArray(parsedData.days)) {
        parsedData.days.forEach(day => {
          if (day.activities && Array.isArray(day.activities)) {
            day.activities.forEach(activity => {
              if (activity.location && typeof activity.location === 'object') {
                const city = activity.location.cityInEnglish || activity.location.city;
                const country = activity.location.countryInEnglish || activity.location.country;
                const displayCity = activity.location.city;
                const displayCountry = activity.location.country;
                
                if (city) {
                  const apiKey = `${city}, ${country || ''}`;
                  const displayKey = `${displayCity}, ${displayCountry || ''}`;
                  cityMap.set(apiKey, displayKey);
                }
              }
            });
          }
        });
      }
      
      // Convert to array of objects with apiCity (English) and displayCity (translated)
      const citiesArray = Array.from(cityMap.entries())
        .map(([apiCity, displayCity]) => ({
          apiCity: apiCity.trim(),
          displayCity: displayCity.trim()
        }))
        .filter(city => city.apiCity && city.apiCity !== ',');
      
      clog('Extracted cities (API/Display):', citiesArray);
      return citiesArray;
    } catch (err) {
      cerror('Error extracting cities:', err);
      return [];
    }
  }, [parsedData]);
  
  // Reset dataFetched when parsedData changes
  useEffect(() => {
    setDataFetched(false);
    setActiveCityTab(null);
  }, [parsedData]);

  // Set default active city tab when cities are available
  useEffect(() => {
    if (cities.length > 0 && activeCityTab === null) {
      setActiveCityTab(`city-0`);
    }
  }, [cities, activeCityTab]);
  
  // Fetch weather data when tab becomes active and cities exist
  useEffect(() => {
    if (isActive && cities.length > 0 && !dataFetched) {
      clog('Weather tab is active, fetching data');
      fetchWeatherForCities(cities);
      setDataFetched(true);
    }
  }, [isActive, cities, dataFetched]);
  
  // Fetch weather data for all cities
  const fetchWeatherForCities = async (cityList) => {
    setLoading(true);
    setError('');
    
    try {
      const weatherResults = {};
      
      // Process each city - use apiCity (English) for API calls, but store by displayCity for UI
      for (const cityObj of cityList) {
        try {
          const response = await fetchWeatherData({
            city: cityObj.apiCity, // Use English name for API call
            days: 4 // 4-day forecast
          });
          // Store by displayCity so UI can find it by the translated name
          weatherResults[cityObj.displayCity] = response;
        } catch (err) {
          cerror(`Error fetching weather for ${cityObj.apiCity}:`, err);
          weatherResults[cityObj.displayCity] = { error: err.message || t('planDetail.weather.errorLoading') };
        }
      }
      
      setWeatherData(weatherResults);
    } catch (err) {
      cerror('Error fetching weather data:', err);
      setError(t('planDetail.weather.errorLoading'));
    } finally {
      setLoading(false);
    }
  };
  
  // Render weather icon based on condition
  const renderWeatherIcon = (condition) => {
    const lowerCondition = condition ? condition.toLowerCase() : '';
    
    // Map conditions to bootstrap icons
    if (lowerCondition.includes('clear') || lowerCondition.includes('sunny')) {
      return <i className="bi bi-sun text-warning fs-2"></i>;
    } else if (lowerCondition.includes('partly cloudy')) {
      return <i className="bi bi-cloud-sun fs-2"></i>;
    } else if (lowerCondition.includes('cloud') || lowerCondition.includes('overcast')) {
      return <i className="bi bi-cloud fs-2"></i>;
    } else if (lowerCondition.includes('fog')) {
      return <i className="bi bi-cloud-fog fs-2"></i>;
    } else if (lowerCondition.includes('drizzle') || lowerCondition.includes('light rain') || lowerCondition.includes('rain showers')) {
      return <i className="bi bi-cloud-drizzle fs-2"></i>;
    } else if (lowerCondition.includes('rain')) {
      return <i className="bi bi-cloud-rain fs-2"></i>;
    } else if (lowerCondition.includes('snow')) {
      return <i className="bi bi-snow fs-2"></i>;
    } else if (lowerCondition.includes('thunder')) {
      return <i className="bi bi-lightning fs-2"></i>;
    } else {
      return <i className="bi bi-cloud fs-2"></i>;
    }
  };
  
  // If tab is not active, show a placeholder message or return null
  if (!isActive) {
    return (
      <div className="text-center p-5">
        <i className="bi bi-cloud-sun text-muted display-4 mb-3"></i>
        <p className="text-muted">{t('planDetail.weather.willBeLoaded')}</p>
      </div>
    );
  }

  if (loading && dataFetched) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status" />
        <p className="mt-3">{t('planDetail.weather.loadingForecast')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>{t('planDetail.weather.errorLoading')}</Alert.Heading>
        <p>{error}</p>
      </Alert>
    );
  }

  if (cities.length === 0) {
    return (
      <Alert variant="info">
        <Alert.Heading>{t('planDetail.weather.noLocationData')}</Alert.Heading>
        <p>{t('planDetail.weather.noCityInfo')}</p>
      </Alert>
    );
  }
  
  // Create tabs array for ScrollableTabs
  const cityTabs = cities.map((cityObj, index) => ({
    eventKey: `city-${index}`,
    title: cityObj.displayCity
  }));

  return (
    <div className="weather-tab">
      <Card className="shadow-sm">
        <Card.Body>
          <Card.Title className="mb-4">
            <i className="bi bi-cloud-sun me-2"></i>
            {t('planDetail.weather.weatherForecast')}
            <span className="text-muted ms-2 small">{t('planDetail.weather.poweredBy')}</span>
          </Card.Title>
          
          {activeCityTab && (
            <ScrollableTabs
              tabs={cityTabs}
              activeTab={activeCityTab}
              onTabChange={setActiveCityTab}
            >
              {cities.map((cityObj, index) => (
                <ScrollableTabs.Tab key={index} eventKey={`city-${index}`}>
                  {weatherData[cityObj.displayCity]?.error ? (
                    <Alert variant="warning">
                      <p className="mb-0">{t('planDetail.weather.failedToLoad', { city: cityObj.displayCity, error: weatherData[cityObj.displayCity].error })}</p>
                    </Alert>
                  ) : !weatherData[cityObj.displayCity] ? (
                    <div className="text-center p-3">
                      <Spinner animation="border" size="sm" />
                      <p className="mt-2">{t('planDetail.weather.loading')}</p>
                    </div>
                  ) : (
                    <div className="weather-forecast">
                      {/* Display local time if available */}
                      {weatherData[cityObj.displayCity]?.timezone && weatherData[cityObj.displayCity]?.timezoneAbbreviation && (
                        <div className="mb-3 p-2 bg-light rounded text-center">
                          <small className="text-muted d-block mb-1">{t('planDetail.weather.localTime')}</small>
                          <strong className="fs-5">{getLocalTime(weatherData[cityObj.displayCity].timezone)}</strong>
                          <span className="text-muted ms-2 small">({weatherData[cityObj.displayCity].timezoneAbbreviation})</span>
                        </div>
                      )}
                      <Row>
                        {weatherData[cityObj.displayCity].forecast && weatherData[cityObj.displayCity].forecast.map((day, dayIndex) => (
                          <Col key={dayIndex} md={3} sm={6} className="mb-3">
                            <Card className="h-100 weather-card">
                              <Card.Body className="text-center">
                                <h5 className="mb-2">
                                  <span className="d-none d-md-inline">{formatDateWithLocale(day.date, 'PPP')}</span>
                                  <span className="d-md-none">{formatDateWithLocale(day.date, 'EEE, MMM d')}</span>
                                </h5>
                                <div className="mb-3">
                                  {renderWeatherIcon(day.condition)}
                                </div>
                                <h4>
                                  {day.tempMax}° <small className="text-muted">{day.tempMin}°</small>
                                </h4>
                                <p className="mb-2">{day.condition}</p>
                                <div className="text-muted small">
                                  {day.humidity !== null && (
                                    <div>{t('planDetail.weather.humidity')}: {day.humidity}%</div>
                                  )}
                                  {day.windSpeed !== null && (
                                    <div>{t('planDetail.weather.wind')}: {day.windSpeed} km/h</div>
                                  )}
                                  <div>{t('planDetail.weather.precipitation')}: {day.precipitation} mm</div>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  )}
                </ScrollableTabs.Tab>
              ))}
            </ScrollableTabs>
          )}
        </Card.Body>
      </Card>
    </div>
  );
} 